// Single source of truth for rendering a thread's self-reported
// `PersistentAgent.statusColor` (its `set_status_color` / `clear_status_color`
// tools).
//
// The enum is a closed traffic light — GREEN / YELLOW / RED, with "no color"
// expressed by absence (null) rather than a member — and this module is the ONE
// place that decides which design token each member resolves to, so re-theming
// the palette never touches stored history. Same posture as
// `board-column-colors.ts`: keyed on the closed enum, static strings only, no
// dynamic token construction.
//
// Consumers: the interaction graph (node ring, selected-node glow, progress pie
// slices) and the orchestration page's thread rows.
import type { PersistentAgentStatusColor } from '@cortex/types';

/**
 * Member → the ui-kit token holding the solid color. Every entry is an existing
 * semantic status token (`packages/ui-kit/tokens.css`) — an agent picks a light,
 * never a raw color, so nothing it reports can render illegibly or off-brand.
 */
export const statusColorToken: Record<PersistentAgentStatusColor, string> = {
  GREEN: 'var(--status-success)',
  YELLOW: 'var(--status-warning)',
  RED: 'var(--status-danger)',
};

/**
 * The same color at reduced strength — for the one element that has to stay
 * distinguishable from its neighbours WITHIN a single tint (the progress ring's
 * current phase, drawn against its completed ones).
 */
export const statusColorSoftToken: Record<PersistentAgentStatusColor, string> = {
  GREEN: 'color-mix(in oklab, var(--status-success) 45%, transparent)',
  YELLOW: 'color-mix(in oklab, var(--status-warning) 45%, transparent)',
  RED: 'color-mix(in oklab, var(--status-danger) 45%, transparent)',
};

/**
 * Operator-facing label for the light — the words behind the color, so the
 * signal is never color-alone (tooltips, accessible names, row captions).
 */
export const statusColorLabel: Record<PersistentAgentStatusColor, string> = {
  GREEN: 'Healthy',
  YELLOW: 'Degraded',
  RED: 'Critical',
};

/**
 * Resolve a thread's tint, tolerating both "no color" (never set, or cleared —
 * both null) and a member this build doesn't know yet: a server one enum member
 * ahead must not blank the node, so an unknown value also falls back to
 * `undefined` and the caller keeps its lifecycle-status coloring.
 */
export function resolveStatusColor(
  color: PersistentAgentStatusColor | null | undefined,
): { solid: string; soft: string; label: string } | undefined {
  if (!color) return undefined;
  const solid = statusColorToken[color];
  if (!solid) return undefined;
  return { solid, soft: statusColorSoftToken[color], label: statusColorLabel[color] };
}
